// "Collection & Invoicing" panel — the right column of the expanded supplier
// row (mockup 4b). Read state shows the stored collection facts + a status
// dot/label header; Edit state is an inline react-hook-form (no modal — the
// panel owns its own edit-mode `useState`, per the Task 10 interface
// contract). Mark closed / Reopen go through small Chakra `Modal` confirm
// dialogs cloned from `unlink-supplier-modal.tsx` / `delete-family-modal.tsx`
// — the legacy redux `ConfirmationDialog` (components/Dialog/ConfirmationDialog.js)
// is only ever used from old Material-UI `.js` pages and requires i18n
// message ids per action name; every modern Chakra TSX page in the repo
// (exclusive-deals, supplier-profiles, master-product-families) confirms
// destructive/irreversible actions with a plain `Modal` + `useDisclosure`
// instead, so that's the pattern mirrored here.
import {
  ReconciliationSupplierDetail,
  useCloseReconciliationApi,
  useReopenReconciliationApi,
  useUpdateCollectionApi,
} from 'apis/reconciliation';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useCustomToast,
  useDisclosure,
} from 'design-system';
import { CommissionAdjustmentCard } from 'pages/reconciliation/components/commission-adjustment-card';
import { MonthlyInvoiceCard } from 'pages/reconciliation/components/monthly-invoice-card';
import {
  formatAed,
  getSavedLabel,
  getSavedLabelColor,
  getStatusLabel,
  STATUS_META,
} from 'pages/reconciliation/utils/format';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiPaperclip } from 'react-icons/fi';
import { useQueryClient } from 'react-query';

const DATE_FORMAT = 'YYYY-MM-DD';

export interface ICollectionPanel {
  month: string;
  detail: ReconciliationSupplierDetail;
  /** Opens the orders modal supplier-wide, "Unpaid only" preset (owned by supplier-row). */
  onViewUnpaidOrders?: () => void;
}

interface FormValues {
  collectedAmount: string;
  collectionDate: string;
  notes: string;
}

const toFormValues = (collection: ReconciliationSupplierDetail['collection']): FormValues => ({
  collectedAmount: collection.collectedAmount !== null ? String(collection.collectedAmount) : '',
  collectionDate: collection.collectionDate ? dayjs(collection.collectionDate).format(DATE_FORMAT) : '',
  notes: collection.notes ?? '',
});

export const CollectionPanel = ({ month, detail, onViewUnpaidOrders }: ICollectionPanel) => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const { collection } = detail;
  const isClosed = detail.status === 'closed';
  // Months with invoices derive collectedAmount from paid invoices — the collection PATCH 400s
  // if we keep sending it. Legacy pre-feature months (no invoices) keep manual entry.
  const hasInvoices = (detail.invoices ?? []).length > 0;
  // `?? open` mirrors getStatusLabel's fallback — an unknown BE status value (deploy gap /
  // future additions) degrades to a stale "Open" look instead of a TypeError blanking the panel.
  const statusMeta = STATUS_META[detail.status] ?? STATUS_META.open;

  const [isEditing, setIsEditing] = useState(false);
  const [invoiceSent, setInvoiceSent] = useState(collection.invoiceSent);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Session-local "✓ Saved just now" flag — mirrors the prototype's `justSavedId`. Reset whenever
  // an edit starts (matches the prototype's `startEdit`/`toggle` clearing `justSavedId`); it also
  // naturally resets on remount since this panel only exists while the row is expanded.
  const [justSaved, setJustSaved] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: toFormValues(collection) });

  const { isOpen: isCloseOpen, onOpen: onCloseOpen, onClose: onCloseClose } = useDisclosure();
  const { isOpen: isReopenOpen, onOpen: onReopenOpen, onClose: onReopenClose } = useDisclosure();

  // Same 3 query families for every mutation below: the detail fetch backing
  // this panel, the suppliers list row (status/collectedAmount columns), and
  // the month's KPI summary.
  const invalidate = () => {
    queryClient.invalidateQueries(['reconciliation-supplier-details', month, detail.supplierId]);
    queryClient.invalidateQueries(['reconciliation-suppliers']);
    queryClient.invalidateQueries(['reconciliation-summary', month]);
  };

  const { mutate: updateCollection, isLoading: isSaving } = useUpdateCollectionApi({
    onSuccess: () => {
      toast({ description: 'Collection details saved', status: 'success' });
      invalidate();
      setFile(null);
      setIsEditing(false);
      setJustSaved(true);
    },
  });

  const { mutate: closeReconciliation, isLoading: isClosing } = useCloseReconciliationApi({
    onSuccess: () => {
      toast({ description: `${detail.supplierName} marked as closed`, status: 'success' });
      invalidate();
      onCloseClose();
      setJustSaved(true);
    },
  });

  const { mutate: reopenReconciliation, isLoading: isReopening } = useReopenReconciliationApi({
    onSuccess: () => {
      toast({ description: `${detail.supplierName} reopened`, status: 'success' });
      invalidate();
      onReopenClose();
      setJustSaved(true);
    },
  });

  const startEdit = () => {
    setInvoiceSent(collection.invoiceSent);
    setFile(null);
    setJustSaved(false);
    reset(toFormValues(collection));
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setFile(null);
    reset(toFormValues(collection));
  };

  const onSubmit = (values: FormValues) => {
    // There's no separate "invoice sent date" input in the edit form (mockup
    // only exposes the Yes/No toggle) — flipping to "Yes" stamps today's date
    // once; flipping back to "No" clears it. An unchanged "Yes" keeps whatever
    // date was already stored.
    //
    // PATCH contract (BE): an OMITTED field is left untouched, an empty string
    // clears it. So every field this form owns is always sent (empty = clear),
    // and collectedAmount is OMITTED on invoice-era months — it is derived from
    // paid invoices there and the BE 400s if it is sent. Omitting used to null
    // it server-side and flip fully-paid months back to "Open"; that BE bug is
    // fixed, and this payload shape is the contract that keeps it fixed.
    const invoiceSentAt = invoiceSent ? dayjs(collection.invoiceSentAt ?? undefined).format(DATE_FORMAT) : '';
    const collectedAmount = hasInvoices
      ? undefined
      : values.collectedAmount.trim() === ''
      ? ''
      : Number(values.collectedAmount);

    updateCollection({
      month,
      supplierId: detail.supplierId,
      invoiceSent,
      invoiceSentAt,
      collectedAmount,
      collectionDate: values.collectionDate || '',
      notes: values.notes,
      file: file ?? undefined,
    });
  };

  const statusLabel = getStatusLabel(detail);

  return (
    <Stack spacing='md'>
      <HStack justify='space-between'>
        <Text fontSize='xs' fontWeight='semibold' color='gray.500' textTransform='uppercase' letterSpacing='0.4px'>
          Collection & Invoicing
        </Text>
        <HStack spacing='xs'>
          <Box w='8px' h='8px' borderRadius='full' bg={statusMeta.dotColor} flexShrink={0} />
          <Text fontSize='sm' fontWeight='medium' color={statusMeta.textColor} whiteSpace='nowrap'>
            {statusLabel}
          </Text>
        </HStack>
      </HStack>

      {isEditing ? (
        <Stack as='form' spacing='md' onSubmit={handleSubmit(onSubmit)}>
          {/* auto-fit gives exactly 2 columns while the panel is ≥ ~416px (2*200+16)
              and collapses to 1 below that — container-width based, so it responds to
              the panel shrinking/wrapping rather than the viewport breakpoint. A 3rd
              column can never appear (it would need 3*200+2*16=632px, above the
              panel's 560px max-width, see supplier-row.tsx). Notes deliberately lives
              OUTSIDE this grid as its own full-width block below: a colSpan={2} cell
              would pin the grid's implicit track count at 2, so auto-fit could never
              actually drop to 1 column (the span forces a second, auto-sized track
              that starves the real columns and overflows the date input on narrow
              panels). */}
          <Grid templateColumns='repeat(auto-fit, minmax(200px, 1fr))' gap='14px 16px'>
            <GridItem minW='0'>
              <FormControl>
                <FormLabel fontSize='sm' fontWeight='semibold' color='gray.600' mb='6px'>
                  Invoice sent
                </FormLabel>
                <ButtonGroup isAttached={false} spacing='6px' size='sm'>
                  <Button
                    type='button'
                    borderRadius='md'
                    onClick={() => setInvoiceSent(true)}
                    bg={invoiceSent ? '#E03E52' : 'white'}
                    color={invoiceSent ? 'white' : 'gray.600'}
                    border='1px solid'
                    borderColor={invoiceSent ? '#E03E52' : 'gray.200'}
                    _hover={{ bg: invoiceSent ? '#c93648' : 'gray.50' }}>
                    Yes
                  </Button>
                  <Button
                    type='button'
                    borderRadius='md'
                    onClick={() => setInvoiceSent(false)}
                    bg={!invoiceSent ? '#E03E52' : 'white'}
                    color={!invoiceSent ? 'white' : 'gray.600'}
                    border='1px solid'
                    borderColor={!invoiceSent ? '#E03E52' : 'gray.200'}
                    _hover={{ bg: !invoiceSent ? '#c93648' : 'gray.50' }}>
                    No
                  </Button>
                </ButtonGroup>
              </FormControl>
            </GridItem>

            {/* minW='0' on this GridItem is load-bearing: without it, the grid track
                sizes itself off the chip's un-truncated content (the filename Text's
                default min-width:auto), so the ellipsis never actually kicks in and
                the whole row gets pushed wider instead. See the matching minW={0} on
                the filename Text below — both ends of the chain are needed. */}
            <GridItem minW='0'>
              <FormControl>
                <FormLabel fontSize='sm' fontWeight='semibold' color='gray.600' mb='6px'>
                  Invoice document
                </FormLabel>
                <HStack
                  spacing='xs'
                  border='1.5px dashed'
                  borderColor='gray.300'
                  borderRadius='md'
                  px='sm'
                  py='xs'
                  cursor='pointer'
                  _hover={{ borderColor: '#E03E52' }}
                  onClick={() => fileInputRef.current?.click()}>
                  <Icon as={FiPaperclip} color='gray.500' boxSize='13px' flexShrink={0} />
                  <Text fontSize='sm' color='gray.600' isTruncated flex='1' minW='0'>
                    {file ? file.name : collection.invoiceDocumentFileName ?? 'No document attached'}
                  </Text>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='.pdf'
                    hidden
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                  <Text fontSize='sm' fontWeight='semibold' color='#E03E52' flexShrink={0}>
                    {file || collection.invoiceDocumentFileName ? 'Replace' : 'Attach'}
                  </Text>
                </HStack>
              </FormControl>
            </GridItem>

            <GridItem minW='0'>
              {hasInvoices ? (
                <FormControl>
                  <FormLabel fontSize='sm' fontWeight='semibold' color='gray.600' mb='6px'>
                    Collected amount (AED)
                  </FormLabel>
                  <Text fontSize='sm' fontWeight='medium' color='gray.700' pt='6px'>
                    {formatAed(collection.collectedAmount ?? 0)}
                  </Text>
                  <Text fontSize='xs' color='gray.400'>
                    Derived from paid invoices — mark invoices paid instead
                  </Text>
                </FormControl>
              ) : (
                <FormControl isInvalid={!!errors.collectedAmount}>
                  <FormLabel fontSize='sm' fontWeight='semibold' color='gray.600' mb='6px'>
                    Collected amount (AED)
                  </FormLabel>
                  <Input
                    type='number'
                    min={0}
                    step='0.01'
                    {...register('collectedAmount', {
                      validate: (value) =>
                        value.trim() === '' || Number(value) >= 0 || 'Collected amount must be 0 or more',
                    })}
                  />
                  <FormErrorMessage>{errors.collectedAmount?.message}</FormErrorMessage>
                </FormControl>
              )}
            </GridItem>

            <GridItem minW='0'>
              <FormControl>
                <FormLabel fontSize='sm' fontWeight='semibold' color='gray.600' mb='6px'>
                  Collection date
                </FormLabel>
                <Input type='date' {...register('collectionDate')} />
              </FormControl>
            </GridItem>
          </Grid>

          {/* Full-width by construction — see the comment on the Grid above for why
              this must NOT be a colSpan cell inside it. Vertical rhythm comes from the
              parent Stack's spacing. */}
          <FormControl>
            <FormLabel fontSize='sm' fontWeight='semibold' color='gray.600' mb='6px'>
              Notes
            </FormLabel>
            <Textarea {...register('notes')} rows={3} />
          </FormControl>

          {/* flexWrap: on very narrow panels the saved-label + buttons can't share a
              line — wrapping the buttons below beats clipping them off the right edge. */}
          <HStack justify='space-between' align='center' flexWrap='wrap' rowGap='xs'>
            <Text fontSize='xs' color={getSavedLabelColor(justSaved)}>
              {getSavedLabel(detail, justSaved)}
            </Text>
            <HStack spacing='sm'>
              <Button variant='outline' onClick={cancelEdit} isDisabled={isSaving}>
                Cancel
              </Button>
              <Button type='submit' colorScheme='red' isLoading={isSaving}>
                Save
              </Button>
            </HStack>
          </HStack>
        </Stack>
      ) : (
        <>
          {/* Same auto-fit contract as the edit grid above — and same reason Notes is
              NOT a colSpan cell in here (a span would pin the implicit track count at
              2 and break the 1-column collapse on narrow panels). */}
          <Grid templateColumns='repeat(auto-fit, minmax(200px, 1fr))' gap='sm' rowGap='md'>
            <GridItem minW='0'>
              <Text fontSize='xs' color='gray.400'>
                Invoice sent
              </Text>
              <Text fontSize='sm' fontWeight='medium' color='gray.700'>
                {collection.invoiceSent
                  ? `Yes${
                      collection.invoiceSentAt ? ` · ${dayjs(collection.invoiceSentAt).format('DD MMM YYYY')}` : ''
                    }`
                  : 'No'}
              </Text>
            </GridItem>

            {/* BE keeps the uploaded file even when the invoice is toggled back to "not
                sent" (it only ever replaces it) — showing the old link next to
                "Invoice sent: No" reads as contradictory, so the row is display-gated
                on invoiceSent. Toggling back to Yes makes the link reappear. */}
            {collection.invoiceSent && (
              <GridItem minW='0'>
                <Text fontSize='xs' color='gray.400'>
                  {hasInvoices ? 'Legacy uploaded invoice' : 'Invoice'}
                </Text>
                {collection.invoiceDocumentUrl ? (
                  <Link
                    href={collection.invoiceDocumentUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    color='gray.700'
                    fontSize='sm'
                    fontWeight='medium'
                    display='flex'
                    alignItems='center'
                    gap='6px'
                    minW='0'>
                    <Icon as={FiPaperclip} boxSize='12px' color='gray.500' flexShrink={0} />
                    <Text as='span' isTruncated minW='0' flex='1'>
                      {collection.invoiceDocumentFileName ?? 'View document'}
                    </Text>
                  </Link>
                ) : (
                  <Text fontSize='sm' color='gray.400'>
                    —
                  </Text>
                )}
              </GridItem>
            )}

            <GridItem minW='0'>
              <Text fontSize='xs' color='gray.400'>
                Collected
              </Text>
              <Text fontSize='sm' fontWeight='medium' color='gray.700'>
                {formatAed(collection.collectedAmount ?? 0)} of {formatAed(detail.invoiceAmount)} AED
              </Text>
              {hasInvoices && (
                <Text fontSize='xs' color='gray.400'>
                  Derived from paid invoices
                </Text>
              )}
            </GridItem>

            <GridItem minW='0'>
              <Text fontSize='xs' color='gray.400'>
                Collection date
              </Text>
              <Text fontSize='sm' fontWeight='medium' color='gray.700'>
                {collection.collectionDate ? dayjs(collection.collectionDate).format('DD MMM YYYY') : '—'}
              </Text>
            </GridItem>
          </Grid>

          <Box>
            <Text fontSize='xs' color='gray.400'>
              Notes
            </Text>
            <Text fontSize='sm' color='gray.700'>
              {collection.notes || '—'}
            </Text>
          </Box>

          <CommissionAdjustmentCard month={month} detail={detail} />

          <MonthlyInvoiceCard month={month} detail={detail} onViewUnpaidOrders={onViewUnpaidOrders} />

          {/* Same wrap-instead-of-clip contract as the edit-state action row above. */}
          <HStack justify='space-between' align='center' flexWrap='wrap' rowGap='xs'>
            <Text fontSize='xs' fontWeight='medium' color={getSavedLabelColor(justSaved)}>
              {getSavedLabel(detail, justSaved)}
            </Text>
            <HStack spacing='sm'>
              {!isClosed && (
                <Button variant='outline' onClick={startEdit}>
                  Edit
                </Button>
              )}
              {isClosed ? (
                <Button variant='outline' onClick={onReopenOpen}>
                  Reopen
                </Button>
              ) : (
                <Button variant='outline' colorScheme='red' onClick={onCloseOpen}>
                  Mark closed
                </Button>
              )}
            </HStack>
          </HStack>
        </>
      )}

      <Modal isOpen={isCloseOpen} onClose={onCloseClose} size='md' closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mark closed</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize='sm'>
              Freeze this month&apos;s figures for <strong>{detail.supplierName}</strong>? Numbers will no longer follow
              order changes.
            </Text>
          </ModalBody>
          <ModalFooter>
            <HStack spacing='sm'>
              <Button variant='outline' onClick={onCloseClose} isDisabled={isClosing}>
                Cancel
              </Button>
              <Button
                colorScheme='red'
                isLoading={isClosing}
                onClick={() => closeReconciliation({ month, supplierId: detail.supplierId })}>
                Mark closed
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isReopenOpen} onClose={onReopenClose} size='md' closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reopen</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize='sm'>Reopen and recompute from live orders?</Text>
          </ModalBody>
          <ModalFooter>
            <HStack spacing='sm'>
              <Button variant='outline' onClick={onReopenClose} isDisabled={isReopening}>
                Cancel
              </Button>
              <Button
                colorScheme='red'
                isLoading={isReopening}
                onClick={() => reopenReconciliation({ month, supplierId: detail.supplierId })}>
                Reopen
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};
